import { lazy, Suspense, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, getColor } from "@wish-ui";
import styled from "@emotion/styled";
import LoadingPage from "components/Fallback/LoadingPage";
import { PTFW_SOURCES } from "views/SetupJourney/constants";
import {
  closeSetupJourneyOverlay,
  openSetupJourneyOverlay,
  unmountSetupJourneyOverlay,
} from "redux/redux";
import {
  SCREEN_MS,
  SCREEN_EASE,
  SCREEN_EDGE_SHADOW,
} from "components/SetupJourneyWidget/entryPointMotion";

/*
 * ---------------------------------------------------------------------------
 * CAT-407 — the setup journey as a full-page overlay presented over Home.
 * ---------------------------------------------------------------------------
 *
 * Mounted once, next to `GlobalOverlays` at the router root, because it has to
 * outlive the route underneath it: the motion spec needs both screens on the
 * page at the same time, and a route swap unmounts Home on the first frame and
 * leaves nothing for the journey to travel against.
 *
 * ## A persistent shell, lazy contents
 *
 * The sliding surface is in the DOM from first paint, parked at
 * translateX(100%). Opening is therefore a state flip against a start value
 * that has been painted for the whole session, which is all a CSS transition
 * needs. Nothing here waits for animation frames.
 *
 * Its *contents* are a different matter and mount only while the journey is on
 * screen. `views/SetupJourney` refetches journey state on mount, fires the
 * streaks WebEngage and Plotline triggers, marks the journey completed and
 * plays a confetti Lottie; a permanently mounted journey would do all of that
 * from under every route in the app.
 *
 * The contents therefore mount on the same commit the slide starts on, so their
 * first paint lands inside the animation. That is the option taken over the two
 * alternatives (mount a frame early, or keep them mounted after the first open)
 * because it is the only one that preserves the journey's
 * `refetchOnMountOrArgChange` contract — every open re-reads the step list,
 * exactly as entering the route used to — and because the first open has to
 * resolve a lazy chunk anyway, which the Suspense fallback below already covers
 * the same way the route did.
 *
 * ## Stacking
 *
 * 41 is the spec's number and it is free — nothing in `src/` uses a z-index
 * between 31 and 49. On its own, though, 41 would lose to Home's own header
 * (200), to the entry point's sticky dock (150) and to the footer (1000). What
 * makes it work is that `SetupJourneyPushSurface` turns the routed page into a
 * stacking context for exactly as long as the journey is on screen, which
 * flattens every z-index inside the page into one layer that 41 sits above.
 *
 * Being *low* is the point, not an accident. Everything that must stay above a
 * full-screen journey is portaled to `document.body` and is therefore outside
 * the page's stacking context: wish-ui `BottomSheet` (1001) — which the
 * journey's own steps open — MUI drawers (1200), the global `Toast` host, and
 * `GlobalOverlays`' force-update wall (99999). A journey that beat those would
 * hide its own bottom sheets.
 *
 * ## The transform trap, from the other side
 *
 * The panel carries a transform at rest, deliberately. That makes it the
 * containing block for the journey's three `position: fixed` descendants — the
 * completed-state CTA bar, the error toast and the confetti layer — so they
 * ride with the screen and pin to the 28rem column instead of to the viewport.
 */

const SetupJourney = lazy(() =>
  import(/* webpackChunkName: "SetupJourney" */ "views/SetupJourney")
);

/** See the stacking note above. Free band: nothing in src/ uses 31–49. */
export const OVERLAY_Z_INDEX = 41;

/**
 * The app's content column. Same value as the journey's own
 * `FixedBottomContainer`, so the two agree about where "the screen" ends.
 */
const OVERLAY_MAX_WIDTH = "28rem";

/** Marks the history entry this overlay pushes, for debugging a stuck stack. */
export const OVERLAY_HISTORY_STATE_KEY = "ptfwSetupJourneyOverlay";

/** The `?source=` the entry was pushed with, so a reload can restore it. */
export const OVERLAY_HISTORY_SOURCE_KEY = "ptfwSetupJourneyOverlaySource";

/*
 * Fixed, viewport-sized, transparent and permanently mounted. Being fixed is
 * what keeps a permanently parked translateX(100%) surface out of normal flow,
 * where it would otherwise be free to widen the document and raise a horizontal
 * scrollbar.
 *
 * Its second job is `overflow: hidden`. The panel parks one panel-width to the
 * right of a 28rem column that is centred, so on any viewport wider than about
 * 56rem the parked journey would sit in plain sight in the right-hand gutter.
 * Clipping is why the panel is absolute *inside* this layer rather than fixed
 * itself: `overflow` on an ancestor only clips a fixed descendant when that
 * ancestor is also its containing block, and making it one would mean putting a
 * transform here — the exact thing the push surface spends its comment avoiding.
 *
 * Pointer-transparent throughout, so the desktop gutters keep belonging to the
 * page underneath.
 */
const ClipLayer = styled(Box)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  /*
   * The clip is the app column, not the viewport. The panel parks at
   * translateX(100%), which is 100% of its OWN width — so if the clip were the
   * full viewport, a 28rem panel on a wide screen would park 448px to the right
   * of centre and sit there in plain view, inside the clip. Matching the clip to
   * the column makes one panel-width exactly one clip-width, so parked is always
   * clipped, at every viewport size. Mirrors the .App column in App.css.
   */
  max-width: ${OVERLAY_MAX_WIDTH};
  margin: 0 auto;
  z-index: ${OVERLAY_Z_INDEX};
  overflow: hidden;
  pointer-events: none;
`;

const JourneyPanel = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  background-color: ${getColor("background.default.default")};
  /* The leading edge sits *above* Home rather than beside it. */
  box-shadow: ${SCREEN_EDGE_SHADOW};

  /* Parked. A surface sitting just off-screen all session must be unreachable
     by taps and by a screen reader; aria-hidden is set alongside this. */
  pointer-events: none;
  transform: translateX(100%);
  transition: transform ${SCREEN_MS}ms ${SCREEN_EASE};

  &[data-open="true"] {
    pointer-events: auto;
    transform: translateX(0);
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &[data-open="true"] {
      transition: none;
    }
  }
`;

/*
 * CAT-407 QA — the scroll lives here, one level below the transform.
 *
 * `JourneyPanel` carries a transform, which makes it the containing block for
 * every `position: fixed` descendant — the completed state's CTA bar and the
 * error toast among them. While it was also the scroller, those bars resolved
 * against a box that scrolled, so they travelled with the stepper instead of
 * staying pinned. They now resolve against the panel, which does not move,
 * and only this inner element scrolls.
 */
const PanelScroller = styled(Box)`
  height: 100%;
  overflow-y: auto;
  /* The journey scrolls, the page behind it must not scroll with it. */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`;

const SetupJourneyOverlay = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isMounted, isOpen, source, hostPath, openCount } = useSelector(
    (state) => state.setupJourneyOverlayReducer
  );

  /*
   * Browser and hardware back.
   *
   * This runs inside a React Native WebView, so the Android hardware back
   * button *is* browser back. Opening therefore pushes one history entry at the
   * current URL; back pops it, which fires `popstate`, which closes the overlay
   * instead of leaving Home. Closing from the UI pops the same entry itself, so
   * the stack is never left one entry deep.
   *
   * `pushedRef` is the single token that says "one entry of ours is on the
   * stack". Every path that consumes the entry clears it *first*, which is what
   * makes a double-close impossible: whichever of `requestClose` and the
   * `popstate` listener runs first takes the token, and the other sees nothing
   * to do.
   */
  const pushedRef = useRef(false);
  /** The route the journey is being presented over. */
  const hostPathRef = useRef(null);
  /** Whether the one-commit grace below has already been spent. */
  const awaitedHostRef = useRef(false);

  /*
   * Push the entry — but not before the host route has settled.
   *
   * Three call sites redirect as they open: the /setup-journey entry point, the
   * collab FTUX form and the intent screen all put the creator on Home first.
   * React 18 batches their dispatch and their `navigate` into one commit, so
   * this usually sees Home already. `hostPath` is the belt to that braces: if
   * the opener named a destination and we are not there yet, skip exactly one
   * commit and anchor on the next one — whatever it turns out to be, so a route
   * guard redirecting somewhere else cannot leave the overlay without an entry.
   *
   * Anchoring to a route the creator is mid-way through leaving would make the
   * very next render look like they had navigated away, and close the journey
   * on the frame it opened.
   */
  useEffect(() => {
    if (!isOpen) {
      pushedRef.current = false;
      hostPathRef.current = null;
      awaitedHostRef.current = false;
      return;
    }
    if (pushedRef.current) return;
    if (hostPath && location.pathname !== hostPath && !awaitedHostRef.current) {
      awaitedHostRef.current = true;
      return;
    }

    pushedRef.current = true;
    hostPathRef.current = location.pathname;
    navigate(`${location.pathname}${location.search}`, {
      state: {
        ...(location.state || {}),
        [OVERLAY_HISTORY_STATE_KEY]: true,
        // Carried so a reload can put the journey back over the right surface.
        [OVERLAY_HISTORY_SOURCE_KEY]: source || PTFW_SOURCES.HOME,
      },
    });
  }, [isOpen, hostPath, location, navigate, source]);

  /*
   * CAT-407 QA — a reload on the pushed entry reopens the journey.
   *
   * Pull-to-refresh reloads the WebView and resets Redux, but history is the
   * browser's and survives: the extra entry stays behind a closed overlay, so
   * the next back lands on a Home that looks dead and leaving the app takes two
   * presses. The entry is marked, so reading the mark on mount restores the
   * state it was pushed for and back means "close the journey" again.
   */
  useEffect(() => {
    if (isOpen || pushedRef.current) return;
    if (!location.state?.[OVERLAY_HISTORY_STATE_KEY]) return;
    pushedRef.current = true;
    awaitedHostRef.current = true;
    hostPathRef.current = location.pathname;
    dispatch(
      openSetupJourneyOverlay({
        source: location.state[OVERLAY_HISTORY_SOURCE_KEY] || PTFW_SOURCES.HOME,
        hostPath: null,
      })
    );
    // Runs on mount only: a restore is a page load, never a later state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPopState = () => {
      // Already consumed by `requestClose`, whose own `navigate(-1)` is what
      // fired this event. Closing again here is the double-close.
      if (!pushedRef.current) return;
      pushedRef.current = false;
      hostPathRef.current = null;
      dispatch(closeSetupJourneyOverlay());
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isOpen, dispatch]);

  // A step CTA navigated off the host route ("Create New Content", "Track
  // Earnings", the engage and DIY step actions). Close, but leave history
  // alone: our entry is buried under the new route now, and popping it would
  // walk the creator backwards out of the page they just asked for.
  useEffect(() => {
    if (!isOpen || !pushedRef.current) return;
    if (location.pathname === hostPathRef.current) return;
    pushedRef.current = false;
    hostPathRef.current = null;
    dispatch(closeSetupJourneyOverlay());
  }, [isOpen, location.pathname, dispatch]);

  // Keep the journey rendered for one screen duration after the close, so the
  // exit has something to carry on its way out.
  useEffect(() => {
    if (isOpen || !isMounted) return undefined;
    const timer = setTimeout(() => {
      dispatch(unmountSetupJourneyOverlay());
    }, SCREEN_MS);
    return () => clearTimeout(timer);
  }, [isOpen, isMounted, dispatch]);

  const requestClose = useCallback(() => {
    if (!pushedRef.current) {
      dispatch(closeSetupJourneyOverlay());
      return;
    }
    // Consume the token before popping: the `popstate` this triggers must find
    // the close already accounted for.
    pushedRef.current = false;
    hostPathRef.current = null;
    dispatch(closeSetupJourneyOverlay());
    navigate(-1);
  }, [dispatch, navigate]);

  return (
    <ClipLayer data-cy="setup-journey-overlay" data-open={String(isOpen)}>
      <JourneyPanel
        data-cy="setup-journey-overlay-panel"
        data-open={String(isOpen)}
        aria-hidden={isOpen ? undefined : "true"}
        role="dialog"
        aria-modal="true"
        aria-label="Finish Setup"
      >
        <PanelScroller>
          {isMounted && (
            <Suspense fallback={<LoadingPage />}>
              {/* Keyed on the open count: the contents stay mounted for one
                  screen duration after a close, so a reopen inside that window
                  would otherwise reuse the instance and skip everything the
                  journey does once per open — the `page_view` among them. */}
              <SetupJourney
                key={openCount}
                onClose={requestClose}
                overlaySource={source || PTFW_SOURCES.HOME}
              />
            </Suspense>
          )}
        </PanelScroller>
      </JourneyPanel>
    </ClipLayer>
  );
};

export default SetupJourneyOverlay;
